import React, { useMemo, useState, useEffect } from 'react';
import { Slider, Switch, Button, Tooltip } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

const GEOMETRY = { cx: 160, cy: 150, r: 100, rText: 75, rArc: 60 };

export const AngleMeasurementWidget = ({ config = {}, variables = {} }) => {
    const allowReflex = config.allowReflex ?? false;
    const showProtractor = config.showProtractor ?? true;
    const [showMeasurement, setShowMeasurement] = useState(config.showMeasurement ?? true);

    const initialAngle = Number.isFinite(variables.angleDegrees) ? variables.angleDegrees : (config.angleDegrees ?? 45);
    const expectedAngle = Number.isFinite(variables.expectedAngle) ? variables.expectedAngle : config.expectedAngle;
    const [angle, setAngle] = useState(initialAngle);

    useEffect(() => setAngle(initialAngle), [initialAngle]);

    const maxAngle = allowReflex ? 360 : 180;

    const { verdict, arcPath, arcColor, pointOnCircle } = useMemo(() => {
        const a = Math.max(0, Math.min(maxAngle, angle));
        let v = null, color = '#3b82f6';

        if (expectedAngle != null) {
            const target = Math.max(0, Math.min(maxAngle, expectedAngle));
            const d = Math.abs(a - target);
            if (d <= 2) { v = 'Correct'; color = '#10b981'; }
            else if (d <= 5) { v = 'Close'; color = '#f59e0b'; }
            else { v = 'Incorrect'; color = '#ef4444'; }
        }

        const rad = (deg) => (deg * Math.PI) / 180;
        const largeArc = a > 180 ? 1 : 0;
        const x2 = GEOMETRY.cx + GEOMETRY.rArc * Math.cos(rad(a));
        const y2 = GEOMETRY.cy - GEOMETRY.rArc * Math.sin(rad(a)); // Y-up logic in math, SVG Y-down handled by subtraction

        // Note: SVG Y is down. -sin(a) moves "up" the screen.
        // Start is 0 deg (East).
        const startX = GEOMETRY.cx + GEOMETRY.rArc;
        const startY = GEOMETRY.cy;

        // Path must use sweep-flag 0 for counter-clockwise in SVG coordinate space (because Y is flipped)
        const path = `M ${GEOMETRY.cx} ${GEOMETRY.cy} L ${startX} ${startY} A ${GEOMETRY.rArc} ${GEOMETRY.rArc} 0 ${largeArc} 0 ${x2} ${y2} Z`;

        return {
            verdict: v,
            arcPath: path,
            arcColor: color,
            pointOnCircle: { x: GEOMETRY.cx + GEOMETRY.r * Math.cos(rad(a)), y: GEOMETRY.cy - GEOMETRY.r * Math.sin(rad(a)) }
        };
    }, [angle, expectedAngle, maxAngle]);

    const renderTicks = () => {
        const count = allowReflex ? 37 : 19;
        return Array.from({ length: count }).map((_, i) => {
            const deg = i * 10;
            const rad = (deg * Math.PI) / 180;
            const isMajor = deg % 30 === 0;
            const rOuter = GEOMETRY.r;
            const rInner = isMajor ? GEOMETRY.r - 12 : GEOMETRY.r - 6;

            const x1 = GEOMETRY.cx + rOuter * Math.cos(rad);
            const y1 = GEOMETRY.cy - rOuter * Math.sin(rad);
            const x2 = GEOMETRY.cx + rInner * Math.cos(rad);
            const y2 = GEOMETRY.cy - rInner * Math.sin(rad);

            const tx = GEOMETRY.cx + GEOMETRY.rText * Math.cos(rad);
            const ty = GEOMETRY.cy - GEOMETRY.rText * Math.sin(rad);

            return (
                <g key={deg}>
                    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#94a3b8" strokeWidth={isMajor?2:1} />
                    {isMajor && (
                        <g>
                            <text x={tx} y={ty} fontSize="9" textAnchor="middle" fill="#64748b">{deg}</text>
                            {allowReflex && deg > 0 && deg < 360 && (
                                <text x={tx} y={ty + 10} fontSize="7" textAnchor="middle" fill="#94a3b8">{360-deg}</text>
                            )}
                        </g>
                    )}
                </g>
            );
        });
    };

    return (
        <div className="flexColumn mediumGap ch-width-100">
            <div className="ch-emph-block" style={{ padding: '20px 0' }}>
                <div className="flexJustifyCentre relative">
                    <svg width="340" height={allowReflex ? "340" : "180"} viewBox={allowReflex ? "0 0 320 320" : "0 0 320 180"} style={{ overflow: 'visible' }}>
                        {showProtractor && (
                            <g>
                                <path d={allowReflex
                                    ? `M ${GEOMETRY.cx + GEOMETRY.r} ${GEOMETRY.cy} A ${GEOMETRY.r} ${GEOMETRY.r} 0 1 0 ${GEOMETRY.cx - GEOMETRY.r} ${GEOMETRY.cy} A ${GEOMETRY.r} ${GEOMETRY.r} 0 1 0 ${GEOMETRY.cx + GEOMETRY.r} ${GEOMETRY.cy}`
                                    : `M ${GEOMETRY.cx - GEOMETRY.r} ${GEOMETRY.cy} A ${GEOMETRY.r} ${GEOMETRY.r} 0 0 1 ${GEOMETRY.cx + GEOMETRY.r} ${GEOMETRY.cy}`
                                } fill="none" stroke="#e2e8f0" strokeWidth="1" />
                                {renderTicks()}
                                <line x1={GEOMETRY.cx - GEOMETRY.r} y1={GEOMETRY.cy} x2={GEOMETRY.cx + GEOMETRY.r} y2={GEOMETRY.cy} stroke="#cbd5e1" strokeWidth="1" />
                            </g>
                        )}
                        <path d={arcPath} fill={arcColor} fillOpacity="0.2" stroke={arcColor} strokeWidth="2" />
                        <line x1={GEOMETRY.cx} y1={GEOMETRY.cy} x2={GEOMETRY.cx + GEOMETRY.r} y2={GEOMETRY.cy} stroke="#334155" strokeWidth="3" />
                        <line x1={GEOMETRY.cx} y1={GEOMETRY.cy} x2={pointOnCircle.x} y2={pointOnCircle.y} stroke="#1e293b" strokeWidth="3" />
                        <circle cx={GEOMETRY.cx} cy={GEOMETRY.cy} r="4" fill="#0f172a" />
                        {showMeasurement && (
                            <g transform={`translate(${GEOMETRY.cx}, ${allowReflex ? GEOMETRY.cy - 20 : GEOMETRY.cy - 30})`}>
                                <rect x="-24" y="-14" width="48" height="24" rx="4" fill="white" stroke="#e2e8f0" />
                                <text x="0" y="3" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#334155">{Math.round(angle)}°</text>
                            </g>
                        )}
                    </svg>
                </div>
            </div>
            <div className="ch-card padding--m">
                <Slider min={0} max={maxAngle} value={angle} onChange={setAngle} trackStyle={{ backgroundColor: arcColor }} />
                <div className="flexSpaceBetween flexAlignCentre border-t--s padding-t--s margin-t--s">
                    <Switch size="small" checked={showMeasurement} onChange={setShowMeasurement} />
                    {expectedAngle !== null && <span className={`ch-text-bold ${verdict==='Correct'?'text-success':'text-danger'}`}>{verdict}</span>}
                </div>
            </div>
        </div>
    );
};